import React, { useEffect, useRef, useState } from 'react';
import { DotLoading, Swiper } from 'antd-mobile';

import { getLotteryAds } from '../api/lottery';
import type { LotteryAdResponse } from '../types/lottery';
import { resolveUploadUrl } from '../utils/uploadUrl';
import './AdCarousel.css';

interface AdCarouselProps {
  position: string;
  className?: string;
  displayMode?: 'carousel' | 'stack';
}

const defaultAdState: LotteryAdResponse = {
  enabled: true,
  fallback_enabled: true,
  fallback_tag: '广告位',
  fallback_title: '广告位招租',
  fallback_subtitle: '品牌推广 · 流量合作 · 资源互换',
  list: [],
};

function isExternalLink(url: string) {
  return /^https?:\/\//i.test(url);
}

function openAdLink(url?: string) {
  const target = String(url || '').trim();
  if (!target) {
    return;
  }
  if (isExternalLink(target)) {
    window.open(target, '_blank', 'noopener,noreferrer');
    return;
  }
  window.location.href = target;
}

function AdCarousel({ position, className = '', displayMode = 'carousel' }: AdCarouselProps) {
  const [adState, setAdState] = useState<LotteryAdResponse>(defaultAdState);
  const [loading, setLoading] = useState(true);
  const requestRef = useRef(0);

  useEffect(() => {
    const requestId = ++requestRef.current;
    setLoading(true);
    getLotteryAds(position, 6)
      .then((items) => {
        if (requestId !== requestRef.current) {
          return;
        }
        setAdState(items || defaultAdState);
      })
      .catch(() => {
        if (requestId !== requestRef.current) {
          return;
        }
        setAdState(defaultAdState);
      })
      .finally(() => {
        if (requestId === requestRef.current) {
          setLoading(false);
        }
      });
  }, [position]);

  const rootClassName = `ad-carousel${className ? ` ${className}` : ''}`;
  const ads = Array.isArray(adState.list) ? adState.list : [];

  if (loading) {
    return (
      <div className={`${rootClassName} is-loading`}>
        <div className="ad-carousel__loading">
          <DotLoading color="primary" />
        </div>
      </div>
    );
  }

  if (!adState.enabled && !adState.fallback_enabled) {
    return null;
  }

  if (!adState.enabled || ads.length === 0) {
    if (!adState.fallback_enabled) {
      return null;
    }
    return (
      <div className={`${rootClassName} is-empty`}>
        <div className="ad-carousel__fallback">
          <div className="ad-carousel__fallback-tag">{adState.fallback_tag || '广告位'}</div>
          <div className="ad-carousel__fallback-title">{adState.fallback_title || '广告位招租'}</div>
          <div className="ad-carousel__fallback-subtitle">
            {adState.fallback_subtitle || '品牌推广 · 流量合作 · 资源互换'}
          </div>
        </div>
      </div>
    );
  }

  // Stack mode: show all images stacked (not carousel)
  if (displayMode === 'stack') {
    return (
      <div className={`${rootClassName} ad-carousel--stack`}>
        {ads.map((item) => (
          <button
            key={`${position}-${item.id}`}
            type="button"
            className="ad-carousel__slide"
            onClick={() => openAdLink(item.link_url)}
          >
            <img
              className="ad-carousel__image"
              src={resolveUploadUrl(item.image)}
              alt={item.title || '广告'}
            />
            {item.title && <span className="ad-carousel__caption">{item.title}</span>}
          </button>
        ))}
      </div>
    );
  }

  return (
    <div className={rootClassName}>
      <Swiper
        autoplay={ads.length > 1}
        autoplayInterval={3200}
        loop={ads.length > 1}
        indicator={(total, current) => (
          <div className="ad-carousel__indicator">
            {Array.from({ length: total }).map((_, index) => (
              <span
                key={index}
                className={`ad-carousel__dot${index === current ? ' is-active' : ''}`}
              />
            ))}
          </div>
        )}
      >
        {ads.map((item) => (
          <Swiper.Item key={`${position}-${item.id}`}>
            <button
              type="button"
              className="ad-carousel__slide"
              onClick={() => openAdLink(item.link_url)}
            >
              <img
                className="ad-carousel__image"
                src={resolveUploadUrl(item.image)}
                alt={item.title || '广告'}
              />
              <span className="ad-carousel__mask" />
              <span className="ad-carousel__caption">{item.title || '广告推荐'}</span>
            </button>
          </Swiper.Item>
        ))}
      </Swiper>
    </div>
  );
}

export default AdCarousel;
